import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { useTranslation } from '../../../i18n';
import type { Announcement } from '../../../types/api';

// STD-015: server state for the Dashboard domain. apiService is imported here
// (a *.queries.ts file) per STD-016. Errors are toasted by the global QueryCache
// handler (lib/queryClient); meta.errorMessage supplies the i18n fallback.

export const dashboardKeys = {
  all: ['dashboard'] as const,
  unreadAnnouncementCount: () => [...dashboardKeys.all, 'unread-announcement-count'] as const,
  announcements: () => [...dashboardKeys.all, 'announcements'] as const,
  dronePictureUrl: (droneSerial: string) =>
    [...dashboardKeys.all, 'drone-picture-url', droneSerial] as const,
  latestFlights: (limit: number, offset: number) =>
    [...dashboardKeys.all, 'latest-flights', limit, offset] as const,
  // Access probes — keyed consistently with access.queries.ts but scoped here
  // for the manufacturing check (UserMenuDropdown needs all three in one place).
  manufacturingAccess: () => [...dashboardKeys.all, 'manufacturing-access'] as const,
};

// Unread announcement badge count — fetched on mount; non-fatal if absent.
export function useUnreadAnnouncementCountQuery(enabled: boolean) {
  return useQuery({
    queryKey: dashboardKeys.unreadAnnouncementCount(),
    enabled,
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => {
      const res = await apiService.getUnreadAnnouncementCount();
      return res.unread_count;
    },
  });
}

// Full announcement list — fetched when the InboxModal opens.
export function useAnnouncementsQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: dashboardKeys.announcements(),
    enabled,
    meta: { errorMessage: t('inbox.failedToLoad') },
    queryFn: async () => {
      const res = await apiService.getUserAnnouncements();
      return res.announcements as Announcement[];
    },
  });
}

// Drone picture presigned URL — non-fatal, falls back to placeholder icon.
export function useDronePictureUrlQuery(droneSerial: string, hasPicture: boolean) {
  return useQuery({
    queryKey: dashboardKeys.dronePictureUrl(droneSerial),
    enabled: !!droneSerial && hasPicture,
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => {
      const res = await apiService.getDronePictureUrl(droneSerial);
      if (res.success && res.picture_url) return res.picture_url;
      return null;
    },
  });
}

// Latest flights list — paginated; initial load gates on isLoading.
export function useLatestFlightsQuery(limit: number, offset: number) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: dashboardKeys.latestFlights(limit, offset),
    meta: { errorMessage: t('overview.failedToLoadFlights') },
    queryFn: async () => {
      const res = await apiService.getLatestFlights(limit, offset);
      if (!res.success) throw new Error('Failed to load latest flights');
      return res.flights;
    },
  });
}

// Manufacturing access probe for UserMenuDropdown.
export function useDashboardManufacturingAccessQuery() {
  return useQuery({
    queryKey: dashboardKeys.manufacturingAccess(),
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => {
      const res = await apiService.checkManufacturingAccess();
      return res.has_access;
    },
  });
}

// Imperative fetchers — called on user actions (click, "show more"). Not hooks;
// exported from here so components never import apiService directly (STD-016).
export async function fetchLatestFlightsPage(limit: number, offset: number) {
  const res = await apiService.getLatestFlights(limit, offset);
  if (!res.success) throw new Error('Failed to load latest flights');
  return res.flights;
}
